import { Spinner } from "@components/ui/spinner";
import { endpoint, useAxiosCall } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import Tooltip from "@ui/external/components/tooltip";
import { printCertificates } from "@utils/function/certificate-print";
import { showToast } from "@utils/helper/index.helper";
import { CertificateData } from "@utils/interface/journey.interface";
import { Printer } from "lucide-react";
import { useState } from "react";

interface PrintCertificateProps {
  /* print a single certificate (certificate listing) */
  certificateId?: number | string;
  /* print every certificate the student holds (student listing) */
  studentId?: number | string;
}

const fetchCertificate = async (id: number | string) => {
  const response: any = await useAxiosCall({
    endpoint: endpoint["certificate"],
    id,
    method: METHODS.GET,
  });
  return response?.data as CertificateData;
};

/* the student payload may nest certificates without the template body, so pull
   the detail for any that came back without html_content */
const resolveTemplates = async (certificates: any[]) =>
  Promise.all(
    certificates.map(async (certificate) =>
      certificate?.certificate_template?.html_content || !certificate?.id
        ? certificate
        : await fetchCertificate(certificate.id)
    )
  );

const PrintCertificate: React.FC<PrintCertificateProps> = ({
  certificateId,
  studentId,
}) => {
  const [isPending, setIsPending] = useState(false);

  const collectCertificates = async () => {
    if (certificateId) {
      const certificate = await fetchCertificate(certificateId);
      return certificate ? [certificate] : [];
    }

    if (studentId) {
      const response: any = await useAxiosCall({
        endpoint: endpoint["student"],
        id: studentId,
        method: METHODS.GET,
      });
      return await resolveTemplates(response?.data?.certificates ?? []);
    }

    return [];
  };

  const handlePrint = async () => {
    if (isPending) return;
    setIsPending(true);

    try {
      const certificates = await collectCertificates();

      if (!certificates.length) {
        showToast({
          title: "Print",
          description: "There is no certificate to print yet",
          type: "warning",
        });
        return;
      }

      await printCertificates(
        certificates,
        certificates.length === 1
          ? `Certificate ${certificates[0]?.certificate_number ?? ""}`.trim()
          : "Certificates"
      );
    } catch (error: any) {
      showToast({
        title: "Print",
        description: error?.message || "Could not prepare the certificate",
        type: "error",
      });
    } finally {
      setIsPending(false);
    }
  };

  const description = studentId
    ? "Print all certificates"
    : "Print certificate";

  return (
    <Tooltip description={isPending ? "Preparing..." : description}>
      <button
        type="button"
        aria-label={description}
        disabled={isPending}
        className="text-gray-500 hover:text-[#1DA077] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
        onClick={handlePrint}
      >
        {isPending ? (
          <Spinner className="w-4 h-4" />
        ) : (
          <Printer size={16} strokeWidth={2} />
        )}
      </button>
    </Tooltip>
  );
};

export default PrintCertificate;
